import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/** Bots vullen onzichtbare velden in en versturen binnen een paar honderd milliseconden. */
const MIN_FILL_MS = 3000;

const quoteSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().min(6).max(40),
  move_date: z.string().max(30).nullable().optional(),
  from_address: z.string().max(300).nullable().optional(),
  to_address: z.string().max(300).nullable().optional(),
  home_type: z.string().max(120).nullable().optional(),
  surface: z.string().max(120).nullable().optional(),
  service: z.string().max(160).nullable().optional(),
  message: z.string().max(4000).nullable().optional(),
  extras: z.array(z.string().max(160)).max(40).default([]),
  needs_lift: z.string().max(40).nullable().optional(),
  volume_m3: z.number().nonnegative().max(500).nullable().optional(),
  inventory: z.record(z.number()).nullable().optional(),
  // Honeypot: onzichtbaar veld, moet leeg blijven.
  company_website: z.string().max(200).optional().default(""),
  // Tijdcontrole: hoe lang het formulier open stond.
  elapsed_ms: z.number().nonnegative().default(0),
});

export const submitQuoteRequest = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => quoteSchema.parse(data))
  .handler(async ({ data }) => {
    const { company_website, elapsed_ms, ...payload } = data;

    if (company_website.trim() !== "" || elapsed_ms < MIN_FILL_MS) {
      const { logSecurityEvent } = await import("@/lib/security.server");
      await logSecurityEvent({
        kind: "form_honeypot",
        path: "/offerte",
        method: "POST",
        details: {
          reason: company_website.trim() !== "" ? "honeypot_filled" : "submitted_too_fast",
          elapsed_ms,
        },
      });
      // Bot krijgt een succesantwoord, maar er wordt niets opgeslagen.
      return { ok: true as const };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("quote_requests").insert({
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      move_date: payload.move_date ?? null,
      from_address: payload.from_address ?? null,
      to_address: payload.to_address ?? null,
      home_type: payload.home_type ?? null,
      surface: payload.surface ?? null,
      service: payload.service ?? null,
      message: payload.message ?? null,
      extras: payload.extras,
      needs_lift: payload.needs_lift ?? null,
      volume_m3: payload.volume_m3 ?? null,
      inventory: (payload.inventory ?? null) as never,
    });

    if (error) {
      console.error("[offerte] opslaan mislukt", error);
      throw new Error("Opslaan mislukt");
    }

    // E-mails mogen de aanvraag nooit blokkeren: opslag is al gelukt.
    try {
      const mail = await import("@/lib/email.server");
      const mailData = {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        move_date: payload.move_date ?? null,
        from_address: payload.from_address ?? null,
        to_address: payload.to_address ?? null,
        home_type: payload.home_type ?? null,
        surface: payload.surface ?? null,
        service: payload.service ?? null,
        message: payload.message ?? null,
        extras: payload.extras,
        needs_lift: payload.needs_lift ?? null,
        volume_m3: payload.volume_m3 ?? null,
      };

      const [notificationResults, confirmationResult] = await Promise.all([
        mail.sendInternalQuoteNotification({
          subject: `Nieuwe offerteaanvraag — ${payload.name}${payload.service ? ` (${payload.service})` : ""}`,
          html: mail.quoteNotificationHtml(mailData),
          replyTo: payload.email,
        }),
        mail.sendMail({
          to: [payload.email],
          subject: "Bedankt voor je aanvraag bij Vakkundig Verhuizingen & Transport",
          html: mail.quoteConfirmationHtml(mailData),
          replyTo: mail.MAIL_INBOX,
        }),
      ]);

      const failedRecipients = notificationResults
        .filter(({ result }) => !result.sent)
        .map(({ recipient }) => recipient);
      if (failedRecipients.length > 0) {
        console.error(`[offerte] interne melding mislukt voor ${failedRecipients.join(", ")}`);
      }
      if (!confirmationResult.sent) {
        console.error(`[offerte] klantbevestiging mislukt voor ${payload.email}`);
      }
    } catch (mailError) {
      console.error("[offerte] e-mail verzenden mislukt", mailError);
    }

    return { ok: true as const };
  });
